
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

/**
 * Get a public URL for a file in Supabase Storage
 */
export const getStorageUrl = (path: string, bucket: string = 'public-images'): string => {
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data.publicUrl;
};

/**
 * Check if a file exists in Supabase Storage
 */
export const checkFileExists = async (path: string, bucket: string = 'public-images'): Promise<boolean> => {
  try {
    // Get the directory and filename
    const lastSlashIndex = path.lastIndexOf('/');
    const directory = lastSlashIndex > 0 ? path.substring(0, lastSlashIndex) : '';
    const filename = lastSlashIndex > 0 ? path.substring(lastSlashIndex + 1) : path;
    
    // List files in the directory
    const { data, error } = await supabase.storage
      .from(bucket)
      .list(directory);
    
    if (error || !data) {
      console.error('Error checking if file exists:', error);
      return false;
    }
    
    // Check if the file exists in the directory
    return data.some(file => file.name === filename);
  } catch (error) {
    console.error('Exception checking if file exists:', error);
    return false;
  }
};

/**
 * List available buckets
 */
export const listBuckets = async (): Promise<string[]> => {
  try {
    const { data, error } = await supabase.storage.listBuckets();
    
    if (error) {
      console.error('Error listing buckets:', error);
      return [];
    }
    
    return data.map(bucket => bucket.name);
  } catch (error) {
    console.error('Exception listing buckets:', error);
    return [];
  }
};

/**
 * Check bucket existence and try to use an alternative if not available
 */
export const ensureBucketForUpload = async (preferredBucket: string = 'public-images'): Promise<string> => {
  try {
    const buckets = await listBuckets();
    console.log('Available buckets:', buckets);
    
    // First choice: use the preferred bucket if it exists
    if (buckets.includes(preferredBucket)) {
      return preferredBucket;
    }
    
    // Second choice: try these buckets in order
    const fallbackBuckets = ['business-plans', 'company-assets', 'documents', 'public-images'];
    for (const bucket of fallbackBuckets) {
      if (buckets.includes(bucket)) {
        console.log(`Using fallback bucket: ${bucket} (preferred bucket ${preferredBucket} not available)`);
        return bucket;
      }
    }
    
    // Skip bucket creation attempts due to RLS policy issues
    if (buckets.length === 0) {
      console.log('No buckets found. Skipping bucket creation due to RLS policy restrictions.');
      
      // Instead of trying to create buckets which will fail due to RLS policies,
      // we'll use a mock bucket name and handle the absence gracefully
      console.log('Using fallback approach for storage operations');
      return 'public-images'; // Return a name but don't try to create it
    }
    
    // If we get here, none of our known buckets exist and we couldn't create one
    console.error('No suitable storage buckets found!');
    throw new Error('No suitable storage buckets are available. Please contact an administrator.');
  } catch (error) {
    console.error('Error ensuring bucket for upload:', error);
    throw error;
  }
};

/**
 * Upload a file to Supabase Storage
 */
export const uploadFile = async (
  file: File,
  path: string,
  bucket: string = 'public-images'
): Promise<{ url: string; path: string } | null> => {
  try {
    console.log(`Uploading file to ${bucket}/${path}...`);
    
    // First ensure the bucket exists or find an alternative
    const actualBucket = await ensureBucketForUpload(bucket);
    
    // Upload the file
    const { data, error } = await supabase.storage
      .from(actualBucket)
      .upload(path, file, {
        cacheControl: '3600',
        upsert: true // Changed to true to prevent conflicts
      });

    if (error) {
      console.error('Error uploading file:', error);
      // Enhanced error details - improved to show more useful information
      const errorDetails = {
        message: error.message,
        name: error.name,
        details: JSON.stringify(error, null, 2)
      };
      toast.error(`Upload failed: ${errorDetails.message}`);
      
      // Check if it's a permission issue
      if (error.message.includes('policy') || error.message.includes('permission')) {
        toast.error('You may not have permission to upload to this bucket. Try using an external URL instead.');
      }
      
      throw new Error(`Upload failed: ${errorDetails.message}`);
    }

    const { data: urlData } = supabase.storage
      .from(actualBucket)
      .getPublicUrl(data.path);

    return {
      url: urlData.publicUrl,
      path: data.path
    };
  } catch (error) {
    console.error('Exception uploading file:', error);
    if (error instanceof Error) {
      toast.error(error.message);
      throw error; // Rethrow to maintain the error message
    }
    throw new Error('Upload failed due to an unexpected error');
  }
};

/**
 * Delete a file from Supabase Storage
 */
export const deleteFile = async (path: string, bucket: string = 'public-images'): Promise<boolean> => {
  try {
    // Check if the file exists before attempting to delete
    const exists = await checkFileExists(path, bucket);
    if (!exists) {
      console.warn(`File ${path} doesn't exist in bucket ${bucket}, skipping delete`);
      return true; // Return true since the file is already gone
    }
    
    const { error } = await supabase.storage
      .from(bucket)
      .remove([path]);

    if (error) {
      console.error('Error deleting file:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Exception deleting file:', error);
    return false;
  }
};

/**
 * Get a signed URL for a file in Supabase Storage (for private files)
 */
export const getSignedUrl = async (path: string, bucket: string = 'public-images', expiresIn: number = 60): Promise<string | null> => {
  try {
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, expiresIn);

    if (error) {
      console.error('Error creating signed URL:', error);
      return null;
    }

    return data.signedUrl;
  } catch (error) {
    console.error('Exception creating signed URL:', error);
    return null;
  }
};
